import { zodResolver } from '@hookform/resolvers/zod'
import { Paperclip, X } from 'lucide-react'
import { useRef, useState, type ReactNode } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toApiError } from '@/api/errors'
import { applyServerErrors } from '@/components/forms/applyServerErrors'
import { FormField } from '@/components/forms/FormField'
import { Button, IconButton } from '@/components/ui/Button'
import { Checkbox } from '@/components/ui/Checkbox'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Textarea } from '@/components/ui/Textarea'
import { useUploadLeaveAttachment } from '../api'
import {
  LEAVE_REQUEST_FIELDS,
  leaveRequestSchema,
  type LeaveRequestFormInput,
  type LeaveRequestFormOutput,
} from '../schemas'
import { formatDays, type LeaveBalance, type LeaveType } from '../types'
import { WORDING, type LeaveWording } from '../wording'

interface Props {
  formId: string
  types: LeaveType[]
  balances?: LeaveBalance[]
  /** Resolves on success; rejects with the API error so 422s map onto fields. */
  onSubmit: (values: LeaveRequestFormOutput) => Promise<unknown>
  onSubmittingChange?: (submitting: boolean) => void
  /** Extra controls rendered first (e.g. the admin's person picker). */
  children?: ReactNode
  /** Students and families ask for an absence rather than leave. */
  wording?: LeaveWording
}

/**
 * Leave request form shared by the staff portal, the family portal and the admin "file on
 * behalf" dialog. Working days are computed by the server from the school calendar.
 */
export function LeaveRequestForm({
  formId,
  types,
  balances,
  onSubmit,
  onSubmittingChange,
  children,
  wording = 'leave',
}: Props) {
  const { t } = useTranslation('leave')
  const upload = useUploadLeaveAttachment()
  const fileInput = useRef<HTMLInputElement>(null)
  const [fileName, setFileName] = useState<string | null>(null)
  const {
    register,
    control,
    handleSubmit,
    setError,
    setValue,
    watch,
    clearErrors,
    formState: { errors },
  } = useForm<LeaveRequestFormInput, unknown, LeaveRequestFormOutput>({
    resolver: zodResolver(leaveRequestSchema),
    defaultValues: {
      typeId: '',
      startDate: '',
      endDate: '',
      halfDay: false,
      reason: '',
      attachmentFileId: null,
    },
  })

  const typeId = watch('typeId')
  const startDate = watch('startDate')
  const endDate = watch('endDate')
  const type = types.find((lt) => String(lt.id) === typeId)
  const balance = balances?.find((b) => String(b.typeId) === typeId)
  const singleDay = startDate !== '' && startDate === endDate

  const submit = handleSubmit(async (values) => {
    onSubmittingChange?.(true)
    try {
      await onSubmit(values)
    } catch (e) {
      applyServerErrors(e, setError, LEAVE_REQUEST_FIELDS)
    } finally {
      onSubmittingChange?.(false)
    }
  })

  const pickFile = (file: File | undefined) => {
    if (!file) return
    clearErrors('attachmentFileId')
    upload.mutate(file, {
      onSuccess: (stored) => {
        setValue('attachmentFileId', stored.id)
        setFileName(stored.originalName)
      },
      onError: (e) =>
        setError('attachmentFileId', {
          message: toApiError(e).violations[0]?.message ?? toApiError(e).message,
        }),
    })
  }

  return (
    <form id={formId} onSubmit={(e) => void submit(e)} noValidate className="space-y-4">
      {children}
      <FormField
        label={t(WORDING[wording].typeLabel)}
        required
        error={errors.typeId?.message}
        hint={
          type
            ? [
                balance
                  ? balance.remaining === null
                    ? t('No yearly limit')
                    : t('{{days}} left this year', { days: formatDays(balance.remaining) })
                  : type.maxDaysPerYear !== null
                    ? t('{{count}} days per year', { count: type.maxDaysPerYear })
                    : t('No yearly limit'),
                type.requiresAttachment ? t('a supporting document is required') : null,
              ]
                .filter(Boolean)
                .join(' · ')
            : undefined
        }
      >
        <Select
          placeholder={t('Choose a type')}
          options={types.map((lt) => ({ value: lt.id, label: lt.name }))}
          {...register('typeId')}
        />
      </FormField>
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField label={t('First day')} required error={errors.startDate?.message}>
          <Input
            type="date"
            {...register('startDate', {
              onChange: (e: { target: { value: string } }) => {
                if (!endDate || endDate < e.target.value) setValue('endDate', e.target.value)
              },
            })}
          />
        </FormField>
        <FormField label={t('Last day')} required error={errors.endDate?.message}>
          <Input type="date" min={startDate || undefined} {...register('endDate')} />
        </FormField>
      </div>
      <Controller
        control={control}
        name="halfDay"
        render={({ field }) => (
          <div>
            <div className="flex items-center gap-2.5">
              <Checkbox
                id={`${formId}-half`}
                checked={field.value}
                disabled={!singleDay}
                onCheckedChange={(v) => field.onChange(v === true)}
                aria-describedby={`${formId}-half-hint`}
              />
              <label htmlFor={`${formId}-half`} className="text-sm text-ink-soft">
                {t('Half day only')}
              </label>
            </div>
            <p id={`${formId}-half-hint`} className="mt-1 text-xs text-muted">
              {t('Available when the first and last day are the same.')}
            </p>
            {errors.halfDay?.message ? (
              <p className="mt-1 text-xs font-medium text-danger-600" role="alert">
                {t(errors.halfDay.message)}
              </p>
            ) : null}
          </div>
        )}
      />
      <FormField label={t('Reason')} required error={errors.reason?.message}>
        <Textarea rows={3} maxLength={1000} {...register('reason')} />
      </FormField>
      <div className="flex flex-col gap-1.5">
        <span id={`${formId}-file-label`} className="text-[0.8125rem] font-medium text-ink-soft">
          {t('Supporting document')}
          {type?.requiresAttachment ? (
            <span className="ml-0.5 text-danger-600" aria-hidden="true">
              *
            </span>
          ) : (
            ` ${t('(optional)')}`
          )}
        </span>
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={fileInput}
            id={`${formId}-file`}
            type="file"
            className="sr-only"
            tabIndex={-1}
            aria-labelledby={`${formId}-file-label`}
            accept=".pdf,.doc,.docx,image/jpeg,image/png,image/webp"
            onChange={(e) => pickFile(e.target.files?.[0])}
          />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            leftIcon={<Paperclip />}
            loading={upload.isPending}
            aria-describedby={`${formId}-file-hint`}
            onClick={() => fileInput.current?.click()}
          >
            {fileName ? t('Replace file') : t('Attach a file')}
          </Button>
          {fileName ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-surface-sunken py-0.5 pr-1 pl-2.5 text-xs text-ink-soft">
              <span className="max-w-48 truncate">{fileName}</span>
              <IconButton
                label={t('Remove attachment')}
                size="icon-sm"
                icon={<X />}
                onClick={() => {
                  setValue('attachmentFileId', null)
                  setFileName(null)
                  if (fileInput.current) fileInput.current.value = ''
                }}
              />
            </span>
          ) : null}
        </div>
        {errors.attachmentFileId?.message ? (
          <p className="text-xs font-medium text-danger-600" role="alert">
            {t(errors.attachmentFileId.message)}
          </p>
        ) : (
          <p id={`${formId}-file-hint`} className="text-xs text-muted">
            {t('PDF, image or Word document, up to 10 MB.')}
          </p>
        )}
      </div>
      <p className="text-xs text-muted">{t('Only days the school is open are counted.')}</p>
    </form>
  )
}
